/**
 * PriorContractAutocomplete Component
 * Autocomplete picker for selecting a prior contract when entering rolled funds.
 * Shows remaining unallocated balance for each candidate contract.
 * Converts balance to the new contract's currency using last loaded FX rate.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Autocomplete, TextField, Box, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { apiGet, extractApiData } from '../../utils/apiClient';
import PriorContractFormDialog from './PriorContractFormDialog';

// Search across reference, external reference, and investor name
const filterOptions = (options, { inputValue }) => {
  const search = inputValue.toLowerCase();
  if (!search) return options;
  return options.filter((opt) =>
    (opt.reference || '').toLowerCase().includes(search) ||
    (opt.user_reference || '').toLowerCase().includes(search) ||
    (opt.investor_name || '').toLowerCase().includes(search)
  );
};

function PriorContractAutocomplete({
  value,
  onChange,
  excludeId,
  disabled = false,
  error,
  helperText,
  rolledFundsAmount = 0,
  currencyCode = null,
  depositDate = null,
  investorId = null,
  investorName = null,
  // Fallback display when the linked contract isn't in the fetched options
  // (e.g. fully allocated and dropped off the rollable list). Form state
  // still has the link; this lets us show it.
  fallbackReference = null,
  fallbackUserReference = null,
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fxRate, setFxRate] = useState(null);
  const [fxPair, setFxPair] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchRollable = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (excludeId) params.set('exclude_id', excludeId);
        if (depositDate) params.set('deposit_date', depositDate);
        if (investorId) params.set('investor_id', investorId);
        const qs = params.toString();
        const url = '/contracts/rollable-list' + (qs ? `?${qs}` : '');
        const response = await apiGet(url);
        const data = extractApiData(response);
        if (!cancelled) setOptions(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching rollable contracts:', err);
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchRollable();
    return () => { cancelled = true; };
  }, [excludeId, depositDate, investorId]);

  // Resolve display option. Prefer the rich option from the fetched list;
  // fall back to a minimal stub built from form-state references if the
  // linked contract isn't (or no longer is) in the rollable list. Without
  // this fallback the Autocomplete shows empty even though `value` is set,
  // making the user think the link was lost on Back-navigation.
  const fetchedOption = options.find((opt) => opt.id === value) || null;
  const selectedOption = fetchedOption
    || (value && (fallbackReference || fallbackUserReference)
      ? { id: value, reference: fallbackReference || '', user_reference: fallbackUserReference || '' }
      : null);

  // Fetch FX rate when prior contract currency differs from new contract currency
  const fetchFxRate = useCallback(async (priorCcy, newCcy) => {
    if (!priorCcy || !newCcy || priorCcy === newCcy) {
      setFxRate(null);
      setFxPair(null);
      return;
    }
    try {
      // /exchange-rates/current/:pair returns { success, rate: <row> } where
      // row.rate is the numeric rate. Read the row directly — extractApiData
      // looks for .data which this endpoint doesn't use.
      const response = await apiGet(`/exchange-rates/current/${priorCcy}/${newCcy}`);
      const rateValue = response?.rate?.rate;
      if (rateValue !== undefined && rateValue !== null) {
        const num = parseFloat(rateValue);
        if (Number.isFinite(num)) {
          setFxRate(num);
          setFxPair(`${priorCcy}/${newCcy}`);
          return;
        }
      }
      setFxRate(null);
      setFxPair(null);
    } catch (err) {
      console.error(`Error fetching FX rate ${priorCcy}/${newCcy}:`, err);
      setFxRate(null);
      setFxPair(null);
    }
  }, []);

  // Re-fetch FX rate when selection or currency changes
  useEffect(() => {
    if (selectedOption && currencyCode && selectedOption.currency_code !== currencyCode) {
      fetchFxRate(selectedOption.currency_code, currencyCode);
    } else {
      setFxRate(null);
      setFxPair(null);
    }
  }, [selectedOption, currencyCode, fetchFxRate]);

  const handleChange = (_, newValue) => {
    if (newValue && newValue._isCreateHistorical) {
      setCreateDialogOpen(true);
      return;
    }
    onChange(newValue?.id || null, newValue);
  };

  const handleHistoricalSaved = (contractStub) => {
    // Add the new stub to options and select it
    setOptions(prev => [...prev, contractStub]);
    onChange(contractStub.id, contractStub);
    setCreateDialogOpen(false);
  };

  const formatAmount = (amount) => {
    const num = parseFloat(amount);
    if (isNaN(num)) return '0.00';
    return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  const getEffectiveRemaining = (option) => {
    if (!option) return 0;
    return parseFloat(option.remaining_balance) || 0;
  };

  const isCrossCurrency = selectedOption && currencyCode && selectedOption.currency_code !== currencyCode;

  return (
    <>
    <Autocomplete
      options={options}
      value={selectedOption}
      onChange={handleChange}
      loading={loading}
      disabled={disabled}
      filterOptions={(opts, params) => {
        const filtered = filterOptions(opts, params);
        filtered.push({ _isCreateHistorical: true, id: '__create__' });
        return filtered;
      }}
      getOptionLabel={(option) => {
        if (option._isCreateHistorical) return '';
        return option.reference + (option.user_reference ? ` (${option.user_reference})` : '');
      }}
      isOptionEqualToValue={(option, val) => option.id === val?.id}
      renderOption={(props, option) => {
        if (option._isCreateHistorical) {
          const { key, ...restProps } = props;
          return (
            <Box
              component="li"
              key="create-historical"
              {...restProps}
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                color: 'primary.main',
                fontWeight: 600,
              }}
            >
              <AddIcon sx={{ mr: 1, fontSize: 20 }} />
              <Typography variant="body2" color="primary" fontWeight={600}>
                Enter Historical Contract
              </Typography>
            </Box>
          );
        }
        const remaining = getEffectiveRemaining(option);
        const allocated = parseFloat(option.allocated_rolled_amount) || 0;
        const isOverAllocated = remaining < 0;
        const maturityStr = option.maturity_date
          ? new Date(option.maturity_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
          : '';
        return (
          <li {...props} key={option.id}>
            <Box sx={{ width: '100%', py: 0.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" fontWeight="bold">
                  {option.user_reference || option.reference}
                  {option.user_reference && (
                    <Typography component="span" variant="body2" color="textSecondary">
                      {' '}({option.reference})
                    </Typography>
                  )}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {option.currency_code} {formatAmount(option.capital_amount)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="textSecondary">
                  {option.investor_name} &middot; {option.status}{maturityStr ? ` &middot; Matures ${maturityStr}` : ''}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.25 }}>
                <Typography variant="caption" color="textSecondary">
                  Already rolled: {option.currency_code} {formatAmount(allocated)}
                </Typography>
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  color={isOverAllocated ? 'error' : 'success.main'}
                >
                  Remaining: {option.currency_code} {formatAmount(remaining)}
                </Typography>
              </Box>
            </Box>
          </li>
        );
      }}
      renderInput={(params) => {
        const serverRemaining = selectedOption ? getEffectiveRemaining(selectedOption) : null;
        const currentRolled = parseFloat(rolledFundsAmount) || 0;

        // Subtract current form's rolled amount from server remaining to get true remaining
        // For cross-currency: convert server remaining to new currency, then subtract current rolled
        let afterAllocation = null;
        let wouldExceed = false;

        if (selectedOption && serverRemaining !== null) {
          if (isCrossCurrency && fxRate) {
            const convertedServerRemaining = serverRemaining * fxRate;
            afterAllocation = convertedServerRemaining - currentRolled;
          } else if (!isCrossCurrency) {
            afterAllocation = serverRemaining - currentRolled;
          }
          // If no FX rate for cross-currency, we can't compare
          if (afterAllocation !== null) {
            wouldExceed = afterAllocation < 0;
          }
        }

        let computedHelperText = helperText || 'Select the prior contract these funds came from';
        if (selectedOption && serverRemaining !== null) {
          if (isCrossCurrency && fxRate) {
            const convertedServerRemaining = serverRemaining * fxRate;
            computedHelperText = `Remaining after allocation: ${currencyCode} ${formatAmount(convertedServerRemaining - currentRolled)} of ≈ ${currencyCode} ${formatAmount(convertedServerRemaining)} (rate: ${fxPair} ${fxRate.toFixed(4)})`;
          } else if (isCrossCurrency && !fxRate) {
            computedHelperText = `Remaining: ${selectedOption.currency_code} ${formatAmount(serverRemaining)} (no FX rate for ${selectedOption.currency_code}/${currencyCode})`;
          } else {
            const afterAlloc = serverRemaining - currentRolled;
            computedHelperText = `Remaining after allocation: ${selectedOption.currency_code || ''} ${formatAmount(afterAlloc)} of ${formatAmount(selectedOption.capital_amount)}`;
          }
        }

        return (
          <TextField
            {...params}
            label="Prior Contract (Rolled From)"
            error={!!error || wouldExceed}
            helperText={
              error?.message
                ? error.message
                : wouldExceed
                  ? `Warning: Rolled funds exceed remaining balance (${currencyCode} ${formatAmount(afterAllocation)})`
                  : computedHelperText
            }
            InputLabelProps={{ shrink: true }}
          />
        );
      }}
    />
    <PriorContractFormDialog
      open={createDialogOpen}
      onClose={() => setCreateDialogOpen(false)}
      onSaved={handleHistoricalSaved}
      investorId={investorId}
      investorName={investorName}
    />
    </>
  );
}

export default PriorContractAutocomplete;
